import Grid from '@mui/material/Unstable_Grid2';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { usePreconfiguredFetch } from '../../hooks/useProvidePreconfiguredFetch';
import { useProvideSnackBar } from '../../hooks';
import qrImage from '../../style/images/qr.png';
import CloseIcon from '@mui/icons-material/Close';
import { StyledQRCodeCard, StyledQRCodeImageContainer, StyledQRCodeList } from '../Layout/styles';
import { QRCodeListItem } from '../../@types/contractors';
import ConfirmDialog from '../Common/ConfirmDialog';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

type QRCodeTabProps = {
    contractorUuId: string | undefined;
    qrCodes: QRCodeListItem[] | null;
    setQrCodes: Dispatch<SetStateAction<QRCodeListItem[] | null>>;
};

const QR_SERVICE_URL = (window as any).REACT_APP_QR_SERVICE_URL;

export function QRCodeTab({ contractorUuId, qrCodes, setQrCodes }: QRCodeTabProps) {
    const { fetchResource } = usePreconfiguredFetch();
    const { showError, showResponseError, showSuccess } = useProvideSnackBar();

    const [loading, setLoading] = useState(false);
    const [qrCodeToDelete, setQrCodeToDelete] = useState<QRCodeListItem | null>(null);
    const [creating, setCreating] = useState(false);

    const fetchQRCodes = async () => {
        try {
            setLoading(true);
            const response = await fetchResource(`/api/v1/qr/list/${contractorUuId}`);

            if (response.ok) {
                const qrCodeList: QRCodeListItem[] = await response.json();
                setQrCodes(qrCodeList);
            } else {
                showResponseError(response);
            }
        } catch (error) {
            showError('Hiba történt a QR kódok lekérdezése közben');
        } finally {
            setLoading(false);
        }
    };

    const revokeQRCode = async () => {
        if (!qrCodeToDelete) {
            return;
        }

        try {
            const response = await fetchResource(`/api/v1/qr/${qrCodeToDelete.uuid}`, { method: 'DELETE' });

            if (response.ok) {
                setQrCodeToDelete(null);
                setQrCodes(null);
            } else {
                showResponseError(response);
            }
        } catch (error) {
            showError('Hiba történt a QR kód törlése közben');
        }
    };

    const createQRCode = async () => {
        if (!contractorUuId || creating) {
            return;
        }

        try {
            setCreating(true);
            const response = await fetchResource(`/api/v1/qr/${contractorUuId}`, {
                method: 'POST',
            });

            if (response.ok) {
                showSuccess('Qr kód sikeresen létrehozva')
                setQrCodes(null);
            } else {
                showResponseError(response);
            }
        } catch (error) {
            showError('Hiba történt a QR kód létrehozása közben');
        } finally {
            setCreating(false);
        }
    };

    useEffect(() => {
        if (!contractorUuId || qrCodes !== null) {
            return;
        }
        fetchQRCodes();
    }, [contractorUuId, qrCodes]);

    if (loading) {
        return (
            <Grid container p={2} justifyContent='center'>
                <CircularProgress />
            </Grid>
        );
    }

    return (
        <Grid container>
            <Grid xs={12}>
                <Typography variant={'h4'} ml={1} mt={2}>
                    QR Kódok
                </Typography>
            </Grid>
            <Grid xs={12} p={2}>
                <StyledQRCodeList>
                    <StyledQRCodeCard onClick={createQRCode}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                                gridRow: 2,
                            }}>
                            {creating ? <CircularProgress /> : <AddCircleOutlineIcon color='primary' sx={{ fontSize: 80 }} />}
                        </Box>

                        <Typography
                            variant='h6'
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gridRow: 3,
                            }}>
                            Új QR kód
                        </Typography>
                    </StyledQRCodeCard>
                    {qrCodes?.map(qrCode => (
                        <StyledQRCodeCard
                            title='QR kód megtekintése'
                            key={qrCode.uuid}
                            onClick={() => {
                                window.open(`${QR_SERVICE_URL}/v1/qr?id=${qrCode.uuid}`, '_blank', 'noopener,noreferrer');
                            }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                <IconButton
                                    title='QR kód törlése'
                                    size='small'
                                    onClick={event => {
                                        event.stopPropagation();
                                        setQrCodeToDelete(qrCode);
                                    }}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                            <StyledQRCodeImageContainer>
                                <img src={qrImage} alt={qrCode.label} />
                            </StyledQRCodeImageContainer>
                            <Typography variant='h6' sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {qrCode.label}
                            </Typography>
                        </StyledQRCodeCard>
                    ))}
                </StyledQRCodeList>
            </Grid>
            <ConfirmDialog
                open={qrCodeToDelete !== null}
                title='QR kód törlése'
                message={`Biztosan törölni szeretnéd a(z) ${qrCodeToDelete?.label} QR kódot?`}
                confirmText='Törlés'
                cancelText='Mégsem'
                confirmColor='error'
                cancelColor='info'
                onConfirm={revokeQRCode}
                onCancel={() => setQrCodeToDelete(null)}
            />
        </Grid>
    );
}
